import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Reveal, stagger, fadeUp } from "./Reveal";
import { CalendarDays, ArrowUpRight, Tag } from "lucide-react";
import { api } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const ProgramsSection = ({ kind, id, overline, title, subtitle, accent, onRegister }) => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let live = true;
    api.get(`/programs?kind=${kind}`)
      .then((r) => { if (live) setItems(r.data); })
      .catch(() => { if (live) setItems([]); })
      .finally(() => { if (live) setLoaded(true); });
    return () => { live = false; };
  }, [kind]);

  if (loaded && items.length === 0) return null;

  const isBlush = accent === "blush";

  return (
    <section id={id} className={`py-24 sm:py-32 ${isBlush ? "bg-[#FBEEF2]" : ""}`}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <Reveal><span className="overline" style={{ color: isBlush ? "#C77D94" : undefined }}>{overline}</span></Reveal>
            <Reveal delay={0.05}>
              <h2 className="section-title text-3xl sm:text-4xl lg:text-5xl mt-4 max-w-xl leading-tight text-[#20301A]">{title}</h2>
            </Reveal>
          </div>
          {subtitle && <Reveal delay={0.1}><p className="text-sm text-[#55703F] max-w-xs">{subtitle}</p></Reveal>}
        </div>

        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((p) => (
            <motion.div key={p.id} variants={fadeUp} data-testid={`program-card-${p.id}`}
              className="group rounded-3xl overflow-hidden border border-[#EBD4DC] bg-white soft-shadow flex flex-col">
              <div className="relative overflow-hidden aspect-[4/3]">
                <img src={`${BACKEND}${p.image_url}`} alt={p.title} loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                {p.price > 0 ? (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-[#D98BA0] text-white px-3 py-1 text-xs font-medium"><Tag size={12} /> ₹{p.price / 100}</span>
                ) : (
                  <span className="absolute top-3 right-3 rounded-full bg-[#3A7D32] text-[#F2F8E8] px-3 py-1 text-xs font-medium">Free</span>
                )}
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-serif text-2xl text-[#20301A] leading-tight">{p.title}</h3>
                {p.date && <p className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-[#55703F]"><CalendarDays size={13} /> {p.date}</p>}
                {p.description && <p className="mt-3 text-sm text-[#55703F] leading-relaxed flex-1">{p.description}</p>}
                {p.seats > 0 && (
                  <p className={`mt-3 text-xs font-medium ${p.full ? "text-[#C2748C]" : "text-[#3A7D32]"}`} data-testid={`program-spots-${p.id}`}>
                    {p.full ? "Fully booked — join the waitlist" : `${p.spots_left} ${p.spots_left === 1 ? "spot" : "spots"} left`}
                  </p>
                )}
                <button data-testid={`program-register-${p.id}`} onClick={() => onRegister(p)}
                  className="group/btn mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#D98BA0] text-white px-5 py-2.5 text-sm hover:bg-[#c2748c] transition-all">
                  {p.full ? "Join waitlist" : kind === "workshop" ? "Register" : "Claim offer"} <ArrowUpRight size={15} className="group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
